import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Toggle } from '../ui/toggle';

export const HIVE_TYPES = [
  { value: 'langstroth', label: 'Langstroth' },
  { value: 'top_bar', label: 'Top Bar Hive' },
  { value: 'horizontal', label: 'Horizontal Hive' },
  { value: 'layens', label: 'Layens Hive' },
  { value: 'nuc', label: 'NUC' },
  { value: 'flow_hive', label: 'Flow Hive' },
  { value: 'national', label: 'National' },
  { value: 'keepers', label: 'Keepers Hive' },
  { value: 'warre', label: 'Warre Hive' },
  { value: 'queen_mating', label: 'Queen Mating Box' },
  { value: 'other', label: 'Other' },
];

// Default sizes/max_qty for user-defined box type strings
export const BOX_TYPE_DEFAULTS = {
  'Deep': { sizes: ['8', '10'], max_qty: 4 },
  'Medium/Ideal': { sizes: ['8', '10'], max_qty: 4 },
  'Shallow': { sizes: ['8', '10'], max_qty: 4 },
  '8-Frame': { sizes: ['8'], max_qty: 4 },
  '10-Frame': { sizes: ['10'], max_qty: 4 },
  'Full Depth': { sizes: ['8', '10'], max_qty: 4 },
  'WSP': { sizes: ['8', '10'], max_qty: 4 },
  'Half Depth': { sizes: ['8', '10'], max_qty: 4 },
  'Flow Super': { sizes: ['6', '7'], max_qty: 3 },
  'National Deep': { sizes: ['11', '14'], max_qty: 3 },
  'National Shallow': { sizes: ['11'], max_qty: 4 },
  'Commercial': { sizes: ['11', '14'], max_qty: 4 },
  'Nucleus (NUC)': { sizes: ['5', '6'], max_qty: 2 },
  'Top Bar Body': { sizes: ['24', '30'], max_qty: 1 },
};

// Normalize box_types from either admin object format or user string format
export const normalizeBoxTypes = (boxTypes) => {
  if (!boxTypes || boxTypes.length === 0) return [];
  return boxTypes.map(bt => {
    if (typeof bt === 'string') {
      const defaults = BOX_TYPE_DEFAULTS[bt] || { sizes: ['8', '10'], max_qty: 4 };
      return { name: bt, sizes: defaults.sizes, max_qty: defaults.max_qty };
    }
    return bt; // Already an object {name, sizes, max_qty}
  });
};

// Tallest stack allowed per box type
export const STACK_MAX = 10;

export const StackRow = ({ label, sizes, size, qty, maxQty, onSizeChange, onQtyChange }) => (
  <div className="space-y-2">
    <div className="font-medium text-stone-700 text-sm">{label}</div>
    <div className="grid grid-cols-2 gap-3">
      <div>
        <div className="text-xs text-stone-500 mb-1">Frame Size</div>
        <div className="flex gap-1">
          {sizes.map((s) => (
            <Toggle key={s} pressed={size === s} onPressedChange={() => onSizeChange(s)}
              className="h-10 flex-1 data-[state=on]:bg-amber-600 data-[state=on]:text-white text-sm">
              {s}F
            </Toggle>
          ))}
        </div>
      </div>
      <div>
        <div className="text-xs text-stone-500 mb-1"># of Boxes</div>
        <div className="flex items-center gap-2">
          <Toggle pressed={false} disabled={qty <= 0} onPressedChange={() => onQtyChange(Math.max(0, (qty || 0) - 1))}
            className="h-10 w-10 text-lg" aria-label={`Fewer ${label}`} data-testid={`stack-qty-minus-${label}`}>−</Toggle>
          <span className="flex-1 text-center font-bold text-lg text-stone-800" data-testid={`stack-qty-${label}`}>{qty || 0}</span>
          <Toggle pressed={false} disabled={(qty || 0) >= STACK_MAX} onPressedChange={() => onQtyChange(Math.min(STACK_MAX, (qty || 0) + 1))}
            className="h-10 w-10 text-lg" aria-label={`More ${label}`} data-testid={`stack-qty-plus-${label}`}>+</Toggle>
        </div>
      </div>
    </div>
  </div>
);

const HiveTypeConfig = ({ hiveType, config, updateConfig, customBoxTypes, boxConfig = {}, onBoxConfigChange }) => {
  // If custom box types are provided, render dynamic box configuration
  const normalizedBoxTypes = customBoxTypes ? normalizeBoxTypes(customBoxTypes) : [];
  if (normalizedBoxTypes.length > 0 && onBoxConfigChange) {
    return (
      <div className="space-y-4 bg-amber-50 border border-amber-200 rounded-xl p-4 mt-3" data-testid="custom-box-config">
        <div className="font-semibold text-amber-800">Box Stack Configuration</div>
        {normalizedBoxTypes.map((boxType, idx) => {
          const cfg = boxConfig[boxType.name] || { qty: 0, size: boxType.sizes?.[0] || '10' };
          return (
            <StackRow
              key={idx}
              label={boxType.name}
              sizes={boxType.sizes || ['8', '10']}
              size={cfg.size || boxType.sizes?.[0] || '10'}
              qty={cfg.qty || 0}
              maxQty={boxType.max_qty || 4}
              onSizeChange={(s) => onBoxConfigChange(boxType.name, { ...cfg, size: s })}
              onQtyChange={(q) => onBoxConfigChange(boxType.name, { ...cfg, qty: q })}
            />
          );
        })}
      </div>
    );
  }

  switch (hiveType) {
    case 'langstroth':
      return (
        <div className="space-y-4 bg-amber-50 border border-amber-200 rounded-xl p-4 mt-3">
          <div className="font-semibold text-amber-800">Langstroth Configuration</div>
          <StackRow label="Deep Brood" sizes={['8', '9', '10']} size={config.langstroth_deep_brood_size} qty={config.langstroth_deep_brood_qty} maxQty={4}
            onSizeChange={(s) => updateConfig('langstroth_deep_brood_size', s)} onQtyChange={(q) => updateConfig('langstroth_deep_brood_qty', q)} />
          <StackRow label="Medium (Ideal)" sizes={['8', '10']} size={config.langstroth_ideal_super_size} qty={config.langstroth_ideal_super_qty} maxQty={4}
            onSizeChange={(s) => updateConfig('langstroth_ideal_super_size', s)} onQtyChange={(q) => updateConfig('langstroth_ideal_super_qty', q)} />
          <StackRow label="Deep Super" sizes={['8', '10']} size={config.langstroth_deep_super_size} qty={config.langstroth_deep_super_qty} maxQty={6}
            onSizeChange={(s) => updateConfig('langstroth_deep_super_size', s)} onQtyChange={(q) => updateConfig('langstroth_deep_super_qty', q)} />
        </div>
      );
    case 'top_bar':
      return (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 mt-3">
          <div className="font-semibold text-green-800 mb-3">Top Bar Configuration</div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-green-700">Number of Bars</Label><Input type="number" min="1" max="40" value={config.top_bar_count || 24} onChange={(e) => updateConfig('top_bar_count', parseInt(e.target.value))} className="mt-1" /></div>
            <div><Label className="text-green-700">Bar Length (inches)</Label><Input type="number" min="10" max="30" value={config.top_bar_length || 19} onChange={(e) => updateConfig('top_bar_length', parseInt(e.target.value))} className="mt-1" /></div>
          </div>
        </div>
      );
    case 'horizontal':
      return (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mt-3">
          <div className="font-semibold text-blue-800 mb-3">Horizontal Hive Configuration</div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-blue-700">Frame Capacity</Label><Input type="number" min="10" max="40" value={config.horizontal_frame_capacity || 20} onChange={(e) => updateConfig('horizontal_frame_capacity', parseInt(e.target.value))} className="mt-1" /></div>
            <div><Label className="text-blue-700">Frame Type</Label><Input value={config.horizontal_frame_type || 'Deep'} onChange={(e) => updateConfig('horizontal_frame_type', e.target.value)} className="mt-1" /></div>
          </div>
        </div>
      );
    case 'layens':
      return (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mt-3">
          <div className="font-semibold text-orange-800 mb-3">Layens Configuration</div>
          <div><Label className="text-orange-700">Number of Frames</Label><Input type="number" min="6" max="30" value={config.layens_frame_count || 14} onChange={(e) => updateConfig('layens_frame_count', parseInt(e.target.value))} className="mt-1" /></div>
        </div>
      );
    case 'flow_hive':
      return (
        <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-4 mt-3">
          <div className="font-semibold text-cyan-800 mb-3">Flow Hive Configuration</div>
          <div className="space-y-3">
            <StackRow label="Brood Box" sizes={['8', '10']} size={config.flow_deep_brood_size} qty={config.flow_deep_brood_qty} maxQty={2}
              onSizeChange={(s) => updateConfig('flow_deep_brood_size', s)} onQtyChange={(q) => updateConfig('flow_deep_brood_qty', q)} />
            <StackRow label="Flow Super" sizes={['6', '7']} size={config.flow_super_size} qty={config.flow_super_qty} maxQty={3}
              onSizeChange={(s) => updateConfig('flow_super_size', s)} onQtyChange={(q) => updateConfig('flow_super_qty', q)} />
          </div>
        </div>
      );
    case 'national':
      return (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mt-3">
          <div className="font-semibold text-red-800 mb-3">National Hive Configuration</div>
          <div className="space-y-3">
            <div><Label className="text-red-700">Variant</Label>
              <div className="flex gap-1 mt-1">{['Standard', 'Deep (14x12)'].map((s, i) => (<Toggle key={s} pressed={String(config.national_variant) === String(i + 1)} onPressedChange={() => updateConfig('national_variant', String(i + 1))} className="flex-1 h-10 data-[state=on]:bg-red-600 data-[state=on]:text-white text-sm">{s}</Toggle>))}</div>
            </div>
            <StackRow label="Deep (Brood)" sizes={['11']} size={config.national_deep_size} qty={config.national_deep_qty} maxQty={3}
              onSizeChange={(s) => updateConfig('national_deep_size', s)} onQtyChange={(q) => updateConfig('national_deep_qty', q)} />
            <StackRow label="Shallow (Super)" sizes={['10']} size={config.national_shallow_size} qty={config.national_shallow_qty} maxQty={6}
              onSizeChange={(s) => updateConfig('national_shallow_size', s)} onQtyChange={(q) => updateConfig('national_shallow_qty', q)} />
          </div>
        </div>
      );
    case 'warre':
      return (
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 mt-3">
          <div className="font-semibold text-indigo-800 mb-3">Warré Hive Configuration</div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-indigo-700">Number of Boxes</Label><Input type="number" min="1" max="8" value={config.warre_boxes || 3} onChange={(e) => updateConfig('warre_boxes', parseInt(e.target.value))} className="mt-1" /></div>
          </div>
        </div>
      );
    case 'keepers':
      return (
        <div className="bg-stone-100 border border-stone-300 rounded-xl p-4 mt-3">
          <div className="font-semibold text-stone-800 mb-3">Keepers Hive Configuration</div>
          <div className="space-y-3">
            <div><Label className="text-stone-700">Configuration Type</Label>
              <div className="flex gap-1 mt-1">{[['one_queen', 'Single Queen'], ['two_queen', 'Two Queen']].map(([val, lbl]) => (<Toggle key={val} pressed={config.keepers_type === val} onPressedChange={() => updateConfig('keepers_type', val)} className="flex-1 h-10 data-[state=on]:bg-stone-600 data-[state=on]:text-white text-sm">{lbl}</Toggle>))}</div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-stone-700 text-xs">Deep Brood</Label><Input type="number" min="0" max="3" value={config.keepers_langstroth_deep_qty || 0} onChange={(e) => updateConfig('keepers_langstroth_deep_qty', parseInt(e.target.value))} className="mt-1" /></div>
              <div><Label className="text-stone-700 text-xs">Medium NUC</Label><Input type="number" min="0" max="3" value={config.keepers_medium_nuc_qty || 0} onChange={(e) => updateConfig('keepers_medium_nuc_qty', parseInt(e.target.value))} className="mt-1" /></div>
              <div><Label className="text-stone-700 text-xs">Deep Super</Label><Input type="number" min="0" max="4" value={config.keepers_super_deep_qty || 0} onChange={(e) => updateConfig('keepers_super_deep_qty', parseInt(e.target.value))} className="mt-1" /></div>
            </div>
          </div>
        </div>
      );
    case 'nuc':
      return (
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mt-3">
          <div className="font-semibold text-purple-800 mb-3">NUC Configuration</div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-purple-700">Frame Count</Label>
              <div className="flex gap-1 mt-1">{[3, 4, 5, 6].map(n => (<Toggle key={n} pressed={parseInt(config.nuc_frames) === n} onPressedChange={() => updateConfig('nuc_frames', String(n))} className="flex-1 h-10 data-[state=on]:bg-purple-600 data-[state=on]:text-white">{n}F</Toggle>))}</div>
            </div>
          </div>
        </div>
      );
    case 'queen_mating':
      return (
        <div className="bg-pink-50 border border-pink-200 rounded-xl p-4 mt-3">
          <div className="font-semibold text-pink-800 mb-3">Queen Mating Box Configuration</div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-pink-700">Compartments</Label><Input type="number" min="1" max="4" value={config.mating_compartments || 1} onChange={(e) => updateConfig('mating_compartments', parseInt(e.target.value))} className="mt-1" /></div>
            <div><Label className="text-pink-700">Frame Size</Label>
              <div className="flex gap-1 mt-1">{['Mini', 'Medium', 'Deep'].map(s => (<Toggle key={s} pressed={config.mating_frame_size === s} onPressedChange={() => updateConfig('mating_frame_size', s)} className="flex-1 h-10 data-[state=on]:bg-pink-600 data-[state=on]:text-white text-sm">{s}</Toggle>))}</div>
            </div>
          </div>
        </div>
      );
    case 'other':
      return (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mt-3">
          <div className="font-semibold text-gray-800 mb-3">Custom Hive Description</div>
          <div><Label className="text-gray-700">Description</Label><Input value={config.other_description || ''} onChange={(e) => updateConfig('other_description', e.target.value)} placeholder="Describe your hive type..." className="mt-1" /></div>
        </div>
      );
    default:
      return null;
  }
};

export default HiveTypeConfig;
